import React, { useEffect, useContext, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import showCollapsibleIcon from "./collapsible-icons/show-collapsible.svg";
import closeCollapsibleIcon from "./collapsible-icons/close-collapsible.svg";
import IconButton from "@mui/material/IconButton";
import UploadIcon from "../../icons/upload_btn.svg";
import Hamber from "../../icons/humber.svg";
import "./viewer-collapsible-sidebar.scss";
import { toggleSidebarAction } from "../../../../actions/global-action";
import OpenSeadragon from "../../utils/fabricjs-overlay";
import {
  getAnnotationArea,
  valuesObject,
  keysObject,
} from "../../../../utils/utils";
import { SocketContext } from "../../../../utils/serviceContext";
import { Tooltip } from "react-tooltip";
import AnnotationUploadPopup from "./annotation-upload-popup";
import CopyIconSuccess from "../../icons/clipboard-approve.png";
import CopyIcon from "../../icons/board-copy.svg";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Notify, SingleSelect } from "../../../../components";
import {
  getSlideHeatMapDataAction,
  updateDescriptionAction,
  resetDownloadAnnotationAction,
  getSlideLabelsAction,
  updateViewerLabelsAction,
  getBlockImagesAction,
  selectBlockImageAction,
  resetBlockImagesAction,
} from "../../actions/viewer-action";
import EditAnnoDetails from "../../icons/edit-anno-details.svg";
import SaveAnno from "../../icons/save-icon.svg";
import EditAnnoLine from "../../icons/edit-line.svg";
import Download from "../../icons/save-line.svg";
import { connect } from "react-redux";
import ImageTag from "../../../slidebox/components/SlideItems/ImageTag";
import moment from "moment";
import DefaultAvatar from "../../../../assets/icons/default-avatar.svg";
import Menu from "@mui/material/Menu";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import UserProfilePopup from "../../../profile/components/UserProfilePopup";
import Can from "../../../../hoc/FullControllHoc/can";
import { showAnalysisResultInViewerAction } from "../../../aiAnalytics/actions/aiAnalytics-action";
import EyeIcon from "../../icons/eye-open.svg";
import EyeCloseIcon from "../../icons/eye-close.svg";
import { FeatureGate } from "../../../../utils/FeatureGate";
import { useDateFormat } from "../../../adminDashboard/components/DashboardItems/useFormat";
import EditIcon from "../../../../assets/icons/InfoEdit.svg";
import SaveIcon from "../../../../assets/icons/save.svg";

const useSortableData = (items, config = null) => {
  const [sortConfig, setSortConfig] = useState(config);

  const sortedItems = React.useMemo(() => {
    let sortableItems = [...items];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [items, sortConfig]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  return { sortedListData: sortedItems, requestSort, sortConfig };
};

// Stable empty-array identity for the selector below — see the note there.
const EMPTY_BLOCK_IMAGE_ROWS = [];

const ViewerCollapsibleSidebar = ({
  imageMetadata,
  showCollapsibleSidebar,
  toggleSidebar,
  viewer,
  annotationDownloadUrl,
  downloadAnnotation,
  setDescriptionAction,
  viewerActivityData,
  analysisResult,
  showAnalysisResultInViewer,
  isAnalysis,
  getSlideHeatMapData,
  getAnalysisResultPending,
  resetDownloadAnnotation,
  dxAccountDetails,
  imageData,
  licenseTypeFeatures,
  selectedSlideLabels,
  viewerDataArray,
  updateLabels,
  getSlideLabels,
}) => {
  const socket = useContext(SocketContext);
  const annotationList = useSelector((state) => state.Viewer.annotationData);
  const uniqueSlideId = useSelector((state) => state.Viewer.slideUniqueId);
  const scaleUnit = useSelector((state) => state.Viewer.scaleUnit);
  const scaleRatio = useSelector((state) => state.Viewer.scaleRatio);
  const userDetails = useSelector((state) => state.Global.loggedUserDetails);
  const dispatch = useDispatch();
  const [editDescription, setEditDescription] = useState(false);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [copytoClipboard, setCopytoClipboard] = useState(false);
  const [description, setDescription] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [
    copytoClipboardAnnotationId,
    setCopytoClipboardAnnotationId,
  ] = useState(false);
  const [labels, setLabels] = useState([]);
  const [editLabels, setEditLabels] = useState(false);

  const labelsRef = useRef([]);

  // SectionStar block images.
  // All three states in display order, including any the block has no picture for.
  //
  // The `|| EMPTY_BLOCK_IMAGE_ROWS` is load-bearing, not defensive tidiness. The
  // Viewer slice is persisted to sessionStorage (store.js blacklists only
  // AIAnalytics and SlideBox), and redux-persist's default reconciler replaces the
  // whole slice with the stored copy rather than merging into it. A session saved
  // before this key existed therefore rehydrates it as undefined. A shared constant
  // rather than a fresh [] so useSelector's reference check does not see a new value
  // on every render.
  const blockImageRows = useSelector(
    (state) => state.Viewer.blockImageRows || EMPTY_BLOCK_IMAGE_ROWS
  );
  const blockImagesLoading = useSelector(
    (state) => state.Viewer.blockImagesLoading
  );
  const blockImagesError = useSelector((state) => state.Viewer.blockImagesError);
  const selectedBlockImageIndex = useSelector(
    (state) => state.Viewer.selectedBlockImageIndex
  );
  const blockImagesRequestedFor = useRef(null);
  const blockImagesSlideId = imageData ? imageData.slidebox_slide_id : null;

  const dateFormat =
    dxAccountDetails && dxAccountDetails.date_format
      ? dxAccountDetails.date_format
      : "MM-DD-YYYY";
  const timezone =
    dxAccountDetails && dxAccountDetails.timezone
      ? dxAccountDetails.timezone
      : "";

  const { sortedListData, requestSort, sortConfig } = useSortableData(
    annotationList
  );
  const getClassNamesFor = (name) => {
    if (!sortConfig) {
      return;
    }
    return sortConfig.key === name ? sortConfig.direction : undefined;
  };
  const [modalOpen, setModalOpen] = React.useState(false);
  const [placement, setPlacement] = React.useState();
  const [anchorElForPopup, setAnchorElForPopup] = useState(null);

  const handleSaveLabels = () => {
    const slideId = viewerDataArray[0].config.osdConfig.slideId;
    if (labels.length > 2) {
      Notify({ value: "Maximum 10 labels can be added" });
      return;
    } else {
      updateLabels({
        entityId: slideId,
        entityType: "slide",
        labels: labels.map((element) => {
          return { value: element, label: element };
        }),
      });
      setEditLabels(false);
      Notify({ value: "Labels updated successfully" });
    }
  };

  const handleModalOpen = (
    index,
    firstName,
    lastName,
    userEmail,
    roleName,
    profile,
    userImageSrc,
    newPlacement
  ) => (event) => {
    setAnchorElForPopup(event.currentTarget);
    // setModalOpen((prev) => placement !== newPlacement || !prev);
    setModalOpen(!modalOpen);
    setPlacement(newPlacement);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };
  const handleSelectChange = (selectedOption) => {
    selectedOption !== null
      ? setLabels(selectedOption.map((element) => element.value))
      : setLabels([]);
  };
  useEffect(() => {
    if (annotationDownloadUrl) {
      const host = window.location.origin;
      const newFileUrl = `${host}${annotationDownloadUrl}`;
      downloadFile(newFileUrl);
      resetDownloadAnnotation();
    }
  }, [annotationDownloadUrl]);

  useEffect(() => {
    getSlideLabels({ slideId: viewerDataArray[0].config.osdConfig.slideId });
  }, []);

  useEffect(() => {
    // close sidebar
    //dispatch(toggleSidebarAction(false));
    // open collapsible sidebar contents
    const expansibleItems = document.getElementsByClassName("default_open");
    for (let i = 0; i < expansibleItems.length; i++) {
      toggleExpansible(expansibleItems[i]);
    }

    socket.getAnnotationMessageSubject().subscribe((item) => {
      if (item && item.fromSrc === "toolbar-annotation") {
        // get current active annotation
        let activeAnno = document.querySelectorAll(
          ".viewer-annotation-detail.active"
        );
        if (
          activeAnno &&
          activeAnno.length > 0 &&
          activeAnno[0].getAttribute("data-pk") == item.pk
        ) {
          return;
        }
        collapseAllAnnotations();
        let anno = document.querySelectorAll(
          `.viewer-annotation-detail[data-pk="${item.pk}"]`
        );
        if (anno && anno.length > 0) {
          toggleExpansible(anno[0]);
        }
      }
    });
  }, []);


  const ButtonAttributes = {
    Name: {
      onClick: () => {
        requestSort("text");
        setAnchorEl(null);
      },
    },
    Annotator: {
      onClick: () => {
        requestSort("first_name");
        setAnchorEl(null);
      },
    },
    Type: {
      onClick: () => {
        requestSort("type");
        setAnchorEl(null);
      },
    },
    Created_At: {
      onClick: () => {
        requestSort("createdAt");
        setAnchorEl(null);
      },
    },
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const downloadFile = (newFileUrl) => {
    let element = document.createElement("a");
    element.setAttribute("href", newFileUrl);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const toggleExpansible = (e) => {
    let el = e.target ? e.target : e;
    el.classList.toggle("active");
    let content = el.nextElementSibling;
    if (!content) {
      return false;
    }
    if (content.style.display === "block") {
      content.style.display = "none";
    } else {
      content.style.display = "block";
    }
  };

  const collapseExpansible = (e) => {
    let el = e.target ? e.target : e;
    el.classList.remove("active");
    let content = el.nextElementSibling;
    content.style.display = "none";
  };

  // Switching slides must not leave the previous slide's block images behind, and
  // must allow the new slide to be fetched on next expand.
  useEffect(() => {
    blockImagesRequestedFor.current = null;
    dispatch(resetBlockImagesAction());
  }, [blockImagesSlideId, dispatch]);

  const handleBlockImagesHeaderClick = (headerEl) => {
    // Passed the element rather than the event: toggleExpansible reads e.target,
    // which would be the inner <span> when the click lands on the label text, and
    // its nextElementSibling is not the content div.
    toggleExpansible(headerEl);

    if (!headerEl.classList.contains("active")) {
      return;
    }
    // Fetched lazily on first expand rather than on every viewer load — most slides
    // did not come from SectionStar, and would otherwise pay for a round trip that
    // nobody looks at.
    if (
      blockImagesSlideId &&
      blockImagesRequestedFor.current !== blockImagesSlideId
    ) {
      blockImagesRequestedFor.current = blockImagesSlideId;
      dispatch(getBlockImagesAction(blockImagesSlideId));
    }
  };

  const rotate = (cx, cy, x, y, angle) => {
    var radians = (Math.PI / 180) * angle,
      cos = Math.cos(radians),
      sin = Math.sin(radians),
      nx = cos * (x - cx) - sin * (y - cy) + cx,
      ny = cos * (y - cy) + sin * (x - cx) + cy;
    return [nx, ny];
  };

  const fitViewportToObject = (item) => {
    var radians = (Math.PI / 180) * item.angle,
      cos = Math.cos(radians),
      sin = Math.sin(radians),
      centerX = item.left + (cos * item.width) / 2,
      centerY = item.top + (sin * item.height) / 2;

    const itemXY = rotate(
      centerX,
      centerY,
      item.left,
      item.top,
      -1 * item.angle
    );
    const nVX = itemXY[0] / imageMetadata.width;
    const nVY = itemXY[1] / imageMetadata.width;
    const nVW = (item.width * item.scaleX) / imageMetadata.width;
    const nVH = (item.height * item.scaleY) / imageMetadata.width;
    // Margin scales with the annotation's own size instead of a fixed
    // fraction of the whole slide, so navigating to a small annotation
    // zooms in tight instead of always framing ~20% of the full image.
    const marginX = Math.max(nVW * 0.5, 0.01);
    const marginY = Math.max(nVH * 0.5, 0.01);
    const newBounds = new OpenSeadragon.Rect(
      nVX - marginX,
      nVY - marginY,
      nVW + marginX * 2,
      nVH + marginY * 2
    );

    viewer.viewport.fitBounds(newBounds);
    return newBounds;
  };

  const collapseAllAnnotations = () => {
    const expansibleItems = document.querySelectorAll(
      ".viewer-annotation-detail"
    );
    for (let i = 0; i < expansibleItems.length; i++) {
      collapseExpansible(expansibleItems[i]);
    }
  };

  const onAnnotationSelect = (e, item) => {
    if (!e.target.classList.contains("active")) {
      setEditDescription(false);
      collapseAllAnnotations();
    }
    toggleExpansible(e);
    if (viewer) {
      fitViewportToObject(item);
    }
  };

  const onEditAnnotation = (item) => {
    socket
      .getAnnotationMessageSubject()
      .next({ ...item, fromSrc: "collapsible-sidebar" });
  };

  const onDownloadAnnotation = (item) => {
    const { pk, unique_id } = item;
    downloadAnnotation({
      annotationId: pk,
      annotationUniqueId: unique_id,
    });
  };

  const getAnnotationType = (item) => {
    if (!item || !item.type) {
      return "";
    }

    switch (item.type) {
      case "ellipse":
        return "Ellipse";
      case "arrow":
        return "Arrow";
      case "path":
        return "Path";
      case "rect":
        return "Rectangle";
    }

    return "";
  };

  const setCopytoClipboardValue = () => {
    setCopytoClipboard(true);
    Notify({ value: "Copied to Clipboard!" });
  };

  const copyAnnotationUniqueId = () => {
    setCopytoClipboardAnnotationId(true);
    Notify({ value: "Copied to Clipboard!" });
  };

  const onEditDescription = (item) => {
    setDescription(item);
    setEditDescription(!editDescription);
  };

  const onSaveDescription = (description, slideId, uniqueId) => {
    if (editDescription) {
      setDescriptionAction({
        description: description,
        slideId: slideId,
        uniqueId: uniqueId,
      });
    }
    setEditDescription(!editDescription);
  };

  const timeDifferent = (dt2, dt1) => {
    let diff = (new Date(dt2).getTime() - new Date(dt1).getTime()) / 1000;
    let min = Math.ceil(Math.round(diff / 60));
    if (min >= 60) {
      let num = min;
      let hours = num / 60;
      let rhours = Math.floor(hours);
      let minutes = (hours - rhours) * 60;
      let rminutes = Math.round(minutes);
      let hourData =
        rhours > 1 ? `${rhours}${" "}hours` : `${rhours}${" "}hour`;
      let minData =
        rminutes > 1 ? `${rminutes}${" "}minutes` : `${rminutes}${" "}minutes`;
      return `${" "}for${" "}${hourData} and ${minData}`;
    } else {
      if (min === 0) {
        min = 1;
      }
      return min > 1
        ? `${" "}for${" "}${min}${" "}minutes`
        : `${" "}for${" "}${min}${" "}minute`;
    }
  };

  const ActivityTab = ({ activityData }) => (
    <div className="details-activity-container viewer-activity">
      {activityData.length > 0 ? (
        activityData.map((activity, index) => (
          <div key={index} className="details-activity-block">
            <span className="activity-date">
              {useDateFormat({ date: activity.activity_start_at }) +
                " " +
                useDateFormat({
                  date: activity.activity_start_at,
                  timeFormat: "HH:mm",
                })}
            </span>
            <div className="activity-details">
              <ImageTag
                onMouseOver={handleModalOpen(
                  index,
                  activity.first_name,
                  activity.last_name,
                  activity.first_name,
                  activity.last_name,
                  "",
                  DefaultAvatar,
                  "left-end"
                )}
                onMouseOut={handleModalClose}
                src={DefaultAvatar}
                className="user-profile-image"
              />
              {userDetails.userID === activity.user_id ? (
                <b>You </b>
              ) : (
                <b>{activity.first_name} </b>
              )}
              {"reviewed the slide"}
              {timeDifferent(
                activity.activity_end_at,
                activity.activity_start_at
              )}
            </div>
          </div>
        ))
      ) : (
        <span className="activity-empty">No activity record found</span>
      )}
    </div>
  );

  const showSortingOptions = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const showHeatmapData = (heatMapdata, runId, status) => {
    getSlideHeatMapData(heatMapdata);
    showAnalysisResultInViewer(heatMapdata, runId, status, true);
  };

  return (
    <div
      id="viewer-collapsible-sidebar"
      className={`viewer-collapsible-sidebar-container viewer-sidebar-${
        showCollapsibleSidebar ? "open" : "collapsed"
      }`}
    >
      {/* <UserProfilePopup
        openModal={modalOpen}
        onClose={handleModalClose}
        userDetails={userDetails}
        isDashboard={true}
        isHeader={false}
        placement={placement}
        anchorEl={anchorElForPopup}
      /> */}
      <div className="viewer-collapsible-sidebar-header">
        {showCollapsibleSidebar && <span> SLIDE MANAGER </span>}
        <img
          onClick={toggleSidebar}
          src={
            showCollapsibleSidebar ? closeCollapsibleIcon : showCollapsibleIcon
          }
          alt="menu"
        />
      </div>

      {showCollapsibleSidebar && (
        <div className="viewer-collapsible-sidebar-content">
          <div className="expansible-item">
            <div
              className={
                isAnalysis ? "expansible-header" : "expansible-header" // default_open remove from here to initially showing collapse
              }
              onClick={toggleExpansible}
            >
              <span className="anno-details">Slide Details</span>
            </div>
            <div className="expansible-content">
              <div className="item">
                <div>Filename:</div>
                <div className="file-name">
                  {imageMetadata["aperio.Filename"]
                    ? imageMetadata["aperio.Filename"]
                    : "-"}
                </div>
              </div>

              <div className="item">
                <div>Unique Id:</div>
                <div className="file-name unique_id">
                  {uniqueSlideId && uniqueSlideId.length > 20
                    ? uniqueSlideId.substring(0, 20) + "..."
                    : uniqueSlideId}
                </div>
                <CopyToClipboard
                  text={uniqueSlideId}
                  onCopy={setCopytoClipboardValue}
                >
                  <div
                    className="code-box-slide-unique-id"
                    title="Copy to clipboard"
                  >
                    <img
                      className={copytoClipboard ? "copy-success" : ""}
                      src={copytoClipboard ? CopyIconSuccess : CopyIcon}
                    />
                  </div>
                </CopyToClipboard>
              </div>
              <div className="item">
                <div>Height:</div>
                <div className="file-name">
                  {imageMetadata.height ? imageMetadata.height : "-"}
                </div>
              </div>
              <div className="item">
                <div>Width:</div>
                <div className="file-name">
                  {imageMetadata.width ? imageMetadata.width : "-"}
                </div>
              </div>
              <div className="item">
                <div>Magnification:</div>
                <div className="file-name">
                  {imageMetadata["openslide.objective-power"]
                    ? imageMetadata["openslide.objective-power"]
                    : "-"}
                </div>
              </div>
              <div className="item">
                <div>Resolution:</div>
                <div className="file-name">
                  {imageMetadata["aperio.MPP"]
                    ? imageMetadata["aperio.MPP"]
                    : "-"}
                </div>
              </div>
              <div className="item">
                <div>Stain Type:</div>
                <div className="file-name">{imageData.slide_stain}</div>
              </div>
            </div>
          </div>

          <div className="expansible-item annotation-details">
            <div
              className={isAnalysis ? "expansible-header" : "expansible-header"}
              onClick={toggleExpansible}
            >
              <span className="anno-details">Annotation Details</span>
              <span></span>
              <div className="annotation-details-icons">
                <div className="anno-icons">
                  <Menu
                    id="customized-menu"
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    style={{ color: "#1D1D1B" }}
                    elevation={0}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    transformOrigin={{ vertical: "top", horizontal: "center" }}
                    PaperProps={{ sx: { border: "1px solid #d3d4d5" } }}
                  >
                    {valuesObject(ButtonAttributes).map(
                      ({ icon, type, ...rest }, index) => (
                        <MenuItem
                          className={"dropdown-li"}
                          {...rest}
                          key={index}
                          sx={{ "&.MuiMenuItem-root": { paddingTop: 0, paddingBottom: 0 } }}
                        >
                          {icon}
                          <ListItemText
                            primary={keysObject(ButtonAttributes)[
                              index
                            ].replace(/_/g, " ")}
                          />
                        </MenuItem>
                      )
                    )}
                  </Menu>
                </div>
                <div className="sort-icon" onClick={showSortingOptions}>
                  <img src={Hamber} alt="" />
                </div>
                <img
                  src={UploadIcon}
                  className="upload-annotation"
                  onClick={() => setShowUploadPopup(true)}
                  data-for="annotation-action-tooltip"
                  data-tip="Upload Annotation"
                />
              </div>
            </div>
            <div className="expansible-annotation-details">
              {sortedListData.map((item) => (
                <div key={item.pk} className="no-border">
                  <div
                    data-pk={item.pk}
                    style={{ color: item.stroke ? item.stroke : "black" }}
                    className="expansible-annotation-header header-close viewer-annotation-detail"
                    onClick={(e) => onAnnotationSelect(e, item)}
                  >
                    {item.text ? item.text : "No Description"}
                    {/* <div className="icon-set">
                      <img src={EditAnnoDetails} alt="edit-details" />
                      <img src={EditAnnoLine} alt="edit-anno" />
                      <img src={Download} alt="save-line" />
                    </div> */}
                  </div>

                  <div class="expansible-content">
                    <div className="item">
                      <div>Annotator:</div>
                      <div className="file-name">
                        {item.first_name
                          ? item.first_name + " " + item.surname
                          : "N/A"}
                      </div>
                    </div>

                    <div className="item unique-id-container">
                      <div>Unique Id:</div>
                      <div className="file-name">
                        {item.unique_id && item.unique_id.length > 20
                          ? item.unique_id.substring(0, 10) + "..."
                          : item.unique_id}
                        <CopyToClipboard
                          text={item.unique_id}
                          onCopy={copyAnnotationUniqueId}
                        >
                          <div
                            className="code-box-annotation-unique-id copy"
                            title="Copy to clipboard"
                          >
                            <img
                              className={
                                copytoClipboardAnnotationId
                                  ? "copy-success"
                                  : ""
                              }
                              src={
                                copytoClipboardAnnotationId
                                  ? CopyIconSuccess
                                  : CopyIcon
                              }
                            />
                          </div>
                        </CopyToClipboard>
                      </div>
                    </div>
                    <div className="item">
                      <div>Type:</div>
                      <div className="file-name">{getAnnotationType(item)}</div>
                    </div>
                    {getAnnotationArea(
                      item.type,
                      item.height,
                      item.width,
                      scaleUnit,
                      scaleRatio
                    ) && (
                      <div className="item">
                        <div>Area:</div>
                        <div className="file-name">
                          {getAnnotationArea(
                            item.type,
                            item.height,
                            item.width,
                            scaleUnit,
                            scaleRatio
                          )}
                          <sup>2</sup>
                        </div>
                      </div>
                    )}
                    <div className="item">
                      <div>Description:</div>
                      {editDescription ? (
                        <textarea
                          className="file-name"
                          rows="3"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          style={{ marginBottom: "7px" }}
                        />
                      ) : (
                        <div
                          className="file-name"
                          style={{
                            height: "auto",
                            overflow: "auto",
                          }}
                        >
                          {item.description}
                        </div>
                      )}
                    </div>
                    <div className="item unique-id-container">
                      <div>Created At:</div>
                      <div className="file-name annotation-actions">
                        <span>
                          {" "}
                          {item.createdAt !== null &&
                            moment(item.createdAt).format(dateFormat)}{" "}
                        </span>

                        <div className="item annotation-link">
                          {!editDescription ? (
                            <IconButton
                              color="default"
                              aria-label="info"
                              component="span"
                              onClick={() =>
                                onEditDescription(item.description)
                              }
                              data-for="annotation-action-tooltip"
                              data-tip="Edit Description"
                            >
                              <img src={EditAnnoDetails} />
                            </IconButton>
                          ) : (
                            <IconButton
                              color="#0b569f"
                              aria-label="info"
                              component="span"
                              onClick={() =>
                                onSaveDescription(
                                  description,
                                  item.slide_id,
                                  item.unique_id
                                )
                              }
                              data-for="annotation-action-tooltip"
                              data-tip="Save Description"
                              style={{ backgroundColor: "#e8f0fe" }}
                            >
                              <img src={SaveAnno} />
                            </IconButton>
                          )}
                          {(userDetails.userID === item.user_id ||
                            !item.first_name) && (
                            <IconButton
                              color="default"
                              aria-label="info"
                              component="span"
                              onClick={() => onEditAnnotation(item)}
                              data-for="annotation-action-tooltip"
                              data-tip="Edit Annotation"
                            >
                              <img src={EditAnnoLine} />
                            </IconButton>
                          )}
                          <IconButton
                            color="default"
                            aria-label="info"
                            component="span"
                            onClick={() => onDownloadAnnotation(item)}
                            data-for="annotation-action-tooltip"
                            data-tip="Download Annotation"
                          >
                            <img src={Download} />
                          </IconButton>
                          <Tooltip
                            id="annotation-action-tooltip"
                            place="bottom"
                            type="dark"
                            effect="solid"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Can
            taskContainer="viewer"
            task="activity-log"
            edit={() => (
              <div className="expansible-item slide-activity">
                <div
                  className={
                    isAnalysis ? "expansible-header" : "expansible-header"
                  }
                  onClick={toggleExpansible}
                >
                  <span className="anno-details">Slide Review Log</span>
                </div>
                <div class="expansible-content">
                  <ActivityTab
                    activityData={viewerActivityData.filter(
                      (dataSet) => dataSet.activity_end_at
                    )}
                  />
                </div>
              </div>
            )}
          />

          <div className="expansible-item slide-labels">
            <div
              className="expansible-header"
              onClick={toggleExpansible}
              ref={(ref) => {
                if (ref) labelsRef.current = ref;
              }}
            >
              <span className="anno-details">Labels</span>
              <img
                src={editLabels ? SaveIcon : EditIcon}
                className="edit-labels-icon"
                onClick={() => {
                  editLabels ? handleSaveLabels() : setEditLabels(!editLabels);
                  !labelsRef.current.classList.contains("active") &&
                    labelsRef.current.click();
                }}
                data-for="annotation-action-tooltip"
                data-tip={editLabels ? "Save" : "Edit"}
              />
            </div>
            <div
              class="expansible-content labels-expand"
              style={{ paddingBottom: editLabels ? "120px" : "0px" }}
            >
              {/* <div className="expansible-slide-labels"> */}
              {editLabels ? (
                <SingleSelect
                  defaultValue={
                    selectedSlideLabels && selectedSlideLabels
                      ? selectedSlideLabels.split(",").map((label, index) => ({
                          value: label,
                          label: label,
                        }))
                      : ""
                  }
                  options={[]}
                  handleChange={handleSelectChange}
                  placeholder="Add labels"
                  customStyles={true}
                  style={{ width: "50%", marginTop: 10 }}
                  classname="viewer-labels"
                />
              ) : (
                <div>
                  {selectedSlideLabels
                    ? selectedSlideLabels.split(",").map((label, index) => (
                        <div className="lable_tags" key={index}>
                          <span>{label}</span>
                        </div>
                      ))
                    : ""}
                </div>
              )}
              {/* </div> */}
            </div>
          </div>

          {/* SectionStar block images. Rows are driven by the API response rather
              than a hardcoded list of three, so a block with only some images
              available renders correctly instead of showing dead entries. */}
          <div className="expansible-item block-images">
            <div
              className="expansible-header"
              onClick={(event) =>
                handleBlockImagesHeaderClick(event.currentTarget)
              }
            >
              <span className="anno-details">
                Block Images from SectionStar&trade;
              </span>
            </div>
            <div className="expansible-content">
              {blockImagesLoading && (
                <div className="block-images-message">Loading…</div>
              )}

              {!blockImagesLoading && blockImagesError && (
                <div className="block-images-message">
                  No block images available for this slide.
                </div>
              )}

              {!blockImagesLoading &&
                !blockImagesError &&
                blockImageRows.length === 0 && (
                  <div className="block-images-message">
                    No block images available for this slide.
                  </div>
                )}

              {!blockImagesLoading &&
                !blockImagesError &&
                // rows is a flat list (three states x two types), grouped here by
                // state rather than carried as a separate groups array — a heading
                // is emitted whenever row.state differs from the previous row's.
                // State headings are not clickable: they open nothing and take no
                // hover/selected styling, so they get no onClick and a plain
                // .block-images-state-heading class rather than .block-images-item.
                blockImageRows.map((row, index) => {
                  const previousRow = blockImageRows[index - 1];
                  const isNewState = !previousRow || previousRow.state !== row.state;

                  return (
                    <React.Fragment key={`${row.state}-${row.type}`}>
                      {isNewState && (
                        <div className="block-images-state-heading">
                          {row.stateLabel}
                        </div>
                      )}
                      <div
                        className={`block-images-item${
                          row.available ? "" : " unavailable"
                        }${
                          // Guard on row.available as well: an unavailable row carries
                          // imageIndex null, and selectedBlockImageIndex is also null
                          // while the panel is closed — without this they would match
                          // and every missing row would render as selected.
                          row.available && row.imageIndex === selectedBlockImageIndex
                            ? " selected"
                            : ""
                        }`}
                        onClick={
                          row.available
                            ? () => dispatch(selectBlockImageAction(row.imageIndex))
                            : undefined
                        }
                      >
                        {row.available ? (
                          row.typeLabel
                        ) : (
                          <span className="block-images-item-note">
                            {row.typeLabel} - Unavailable
                          </span>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
            </div>
          </div>

          {/* <div className="analysis-result-container">
            <FeatureGate feature="ai_analysis">
              <Can
                taskContainer="viewer"
                task="activity-log"
                edit={() => (
                  <div className="expansible-item slide-activity">
                    <div
                      className={"expansible-header"}
                      onClick={toggleExpansible}
                    >
                      <span className="anno-details">AI Analysis Result</span>
                    </div>
                    <div class="expansible-content">
                      {analysisResult &&
                        analysisResult.length > 0 &&
                        analysisResult
                          .filter(
                            (filterObj) => filterObj.fabricPoints.length > 0
                          )
                          .map((resultObj, index) => (
                            <div className="analysis-result">
                              <span>
                                {resultObj.alg_id === 1
                                  ? `Nuclei Segmentation ${index + 1}`
                                  : `Orion AI ${index + 1}`}
                              </span>
                              {resultObj.selected && (
                                <img
                                  src={EyeIcon}
                                  className="view-icon"
                                  onClick={() =>
                                    resultObj.alg_id === 1
                                      ? showAnalysisResultInViewer(
                                          resultObj.fabricPoints,
                                          resultObj.runId,
                                          false
                                        )
                                      : showHeatmapData(
                                          resultObj.fabricPoints,
                                          resultObj.runId,
                                          false
                                        )
                                  }
                                />
                              )}
                              {!resultObj.selected && (
                                <img
                                  src={EyeCloseIcon}
                                  className="view-icon"
                                  onClick={() =>
                                    resultObj.alg_id === 1
                                      ? showAnalysisResultInViewer(
                                          resultObj.fabricPoints,
                                          resultObj.runId,
                                          true
                                        )
                                      : showHeatmapData(
                                          resultObj.fabricPoints,
                                          resultObj.runId,
                                          true
                                        )
                                  }
                                />
                              )}
                            </div>
                          ))}
                      {getAnalysisResultPending && <span>Loading...</span>}
                    </div>
                  </div>
                )}
              />
            </FeatureGate>
          </div> */}

          {showUploadPopup && (
            <AnnotationUploadPopup onClose={() => setShowUploadPopup(false)} />
          )}
        </div>
      )}
    </div>
  );
};

ViewerCollapsibleSidebar.propTypes = {
  imageMetadata: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  viewerActivityData: state.Viewer.viewerActivityData,
  analysisResult: state.AIAnalytics.analysisResult,
  getAnalysisResultPending: state.AIAnalytics.getAnalysisResultPending,
  dxAccountDetails: state.Global.dxAccountDetails,
  licenseTypeFeatures: state.Global.loggedUserDetails.licenseTypeAccessData,
  selectedSlideLabels: state.Viewer.selectedSlideLabels,
  viewerDataArray: state.Viewer.viewerDataArray,
});

const mapDispatchToProps = (dispatch) => ({
  setDescriptionAction: (payload, actions) =>
    dispatch(updateDescriptionAction(payload, actions)),
  showAnalysisResultInViewer: (payload, runId, visible, isHeatmap) =>
    dispatch(
      showAnalysisResultInViewerAction(payload, runId, visible, isHeatmap)
    ),
  getSlideHeatMapData: (payload) =>
    dispatch(getSlideHeatMapDataAction(payload)),
  resetDownloadAnnotation: () => dispatch(resetDownloadAnnotationAction()),
  getSlideLabels: (payload) => dispatch(getSlideLabelsAction(payload)),
  updateLabels: (payload) => dispatch(updateViewerLabelsAction(payload)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ViewerCollapsibleSidebar);
